import { translate } from '../../../lib/lang.helper';
import { useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';
import { CheckboxButton, Input, Dropdown } from '../../shared';
import { SelectAssessFields } from '../../../store/extra/extra.selector';
import { ICustomValue } from '../../../entities/automation.entities';
import { Link } from 'react-router-dom';
import { IMusselLineResource } from '../../../entities/farms.entities';
import { getAverageSize, numToStr } from '../../../lib/common.helpers';

interface IFormData {
  size_min?: number | null;
  size_max?: number | null;
  size_avg?: number | null;
  btm_size_min?: number | null;
  btm_size_max?: number | null;
  btm_size_avg?: number | null;
  assess_bottom?: boolean;
  ready_to_harvest?: boolean;
  custom_values?: ICustomValue[];
}

interface Props {
  lineData: IMusselLineResource;
  disabled?: boolean;
  formData: IFormData;
  onChange: (data: any) => void;
}

const InstantMusselAssessment = ({
  lineData,
  disabled,
  formData,
  onChange,
}: Props) => {
  const lang = useSelector(selectLang);

  const customAssesFields = SelectAssessFields(lineData.farm_id);

  const updateCustomAssessValue = (fieldID: number, val: string) => {
    let tmp = [...(formData.custom_values ?? [])];
    const i = tmp.findIndex(x => x.field_id === fieldID);
    const field = customAssesFields.find(x => x.id === fieldID);
    if (!field) return;

    if (i >= 0) {
      tmp[i].field_val = val;
    } else {
      tmp.push({
        field_id: fieldID,
        field_val: val,
      });
    }
    onChange({ ...formData, custom_values: tmp });
  };
  const updateForm = (key: keyof IFormData, value: string) => {
    const val = value.length <= 0 ? null : Number(value);
    let tmp = { ...formData, [key]: val };
    if (
      (key === 'size_min' || key === 'size_max') &&
      tmp.size_min &&
      tmp.size_max
    ) {
      tmp.size_avg = getAverageSize(tmp.size_min, tmp.size_max);
    }
    if (
      (key === 'btm_size_min' || key === 'btm_size_max') &&
      tmp.btm_size_min &&
      tmp.btm_size_max
    ) {
      tmp.btm_size_avg = getAverageSize(tmp.btm_size_min, tmp.btm_size_max);
    }
    onChange(tmp);
  };

  return (
    <div>
      <div className='assessment-modal__condition d-flex justify-between'>
        <div className={'mxw-180'}>
          <Input
            type='number'
            label={`${translate(
              lang,
              formData.assess_bottom ? 'Top size min' : 'Size min',
            )} (mm)`}
            value={numToStr(formData.size_min)}
            onChange={e => updateForm('size_min', e.target.value)}
            required={true}
            disabled={disabled}
          />
        </div>
        <div className={'mxw-180 ml-12 mr-12'}>
          <Input
            type='number'
            label={`${translate(
              lang,
              formData.assess_bottom ? 'Top size max' : 'Size max',
            )} (mm)`}
            value={numToStr(formData.size_max)}
            onChange={e => updateForm('size_max', e.target.value)}
            required={true}
            disabled={disabled}
          />
        </div>
        <div className={'mxw-180'}>
          <Input
            type='number'
            label={`${translate(
              lang,
              formData.assess_bottom ? 'Top size average' : 'Size average',
            )} (mm)`}
            value={numToStr(formData.size_avg)}
            onChange={e => updateForm('size_avg', e.target.value)}
            required={true}
            disabled={disabled}
          />
        </div>
      </div>
      <div className='mb-17'>
        <CheckboxButton
          label={translate(lang, '_measure_top_bottom_separately')}
          checked={formData.assess_bottom}
          onChange={e =>
            onChange({ ...formData, assess_bottom: e.target.checked })
          }
        />
      </div>
      {formData.assess_bottom && (
        <div className='assessment-modal__condition d-flex justify-between'>
          <div className='mxw-180'>
            <Input
              type='number'
              label={`${translate(lang, 'Bottom size min')} (mm)`}
              value={numToStr(formData.btm_size_min)}
              onChange={e => updateForm('btm_size_min', e.target.value)}
              required={true}
              disabled={disabled}
            />
          </div>
          <div className='mxw-180 ml-12 mr-12'>
            <Input
              type='number'
              label={`${translate(lang, 'Bottom size max')} (mm)`}
              value={numToStr(formData.btm_size_max)}
              onChange={e => updateForm('btm_size_max', e.target.value)}
              required={true}
              disabled={disabled}
            />
          </div>
          <div className='mxw-180'>
            <Input
              type='number'
              label={`${translate(lang, 'Bottom size average')} (mm)`}
              value={numToStr(formData.btm_size_avg)}
              onChange={e => updateForm('btm_size_avg', e.target.value)}
              required={true}
              disabled={disabled}
            />
          </div>
        </div>
      )}
      <CheckboxButton
        className='mb-17'
        label={translate(lang, 'Ready to harvest')}
        checked={formData.ready_to_harvest}
        onChange={e =>
          onChange({ ...formData, ready_to_harvest: e.target.checked })
        }
        disabled={disabled}
      />
      {customAssesFields.map(field => {
        const cv = formData.custom_values?.find(x => x.field_id === field.id);

        return (
          <div key={field.id} className='mb-17'>
            {field.input_type === 'select' ? (
              <>
                <Dropdown
                  label={field.name}
                  options={
                    field.options
                      ?.split(',')
                      .map(t => ({ id: t, value: t, label: t })) ?? []
                  }
                  value={cv?.field_val ?? undefined}
                  onChange={v => updateCustomAssessValue(field.id, v)}
                  disabled={disabled}
                />
                {(!field.options || field.options.length <= 0) && (
                  <Link to={'/custom-fields'}>
                    <span style={{ color: '#008ed1' }}>Set up field</span>
                  </Link>
                )}
              </>
            ) : (
              <Input
                type={field.data_type === 'number' ? 'number' : 'text'}
                label={field.name}
                value={cv?.field_val ?? ''}
                onChange={e =>
                  updateCustomAssessValue(field.id, e.target.value)
                }
                unit={field.unit}
                disabled={disabled}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InstantMusselAssessment;
